import { useQuery } from "@tanstack/react-query";
import { pathPickBasketQueryApi } from "@/features/pathpick/features/basket/api/query";
import { PathPickNaverPartnerStatusResponse } from "@/features/pathpick/features/basket/types/response";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import { toCamelKeyObject } from "@/utils/case";
import { isRetryableApiError } from "@/utils/axios-client-error";

// 네이버 제휴(판매자ID) 연결 상태 조회
const useGetNaverPartnerStatus = () => {
  const { companyId } = useCompanyIdentity();

  const {
    data: naverPartnerStatus,
    isPending,
    isFetching,
    dataUpdatedAt,
    isError,
    error,
    errorUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: ["pathpick", "naver-partner-status", companyId],
    queryFn: async () => {
      const data = await pathPickBasketQueryApi.getNaverPartnerStatus(
        companyId as number
      );
      return toCamelKeyObject(data) as PathPickNaverPartnerStatusResponse;
    },
    enabled: Boolean(companyId),
    // 연결은 외부 창(스마트스토어 판매자센터)에서 이뤄져 완료 시점을 알 수 없다.
    // 사용자가 창을 마치고 돌아오는 순간을 신호로 삼아 최신 상태를 가져온다.
    refetchOnWindowFocus: true,
    retry: (failureCount, err) => isRetryableApiError(err) && failureCount < 1,
  });

  // isFetching/dataUpdatedAt/refetch는 연동 안내 모달의 "연결 확인" UI(로딩·마지막 확인 시각)에서 쓴다.
  // isError는 "미연동"과 "확인 불가"를 구분해야 하는 소비처에서 쓴다 —
  // 조회에 실패했는데 미연동으로 보이면 이미 연동된 계정에 다시 연동을 시키게 된다.
  return {
    naverPartnerStatus,
    isPending,
    isFetching,
    dataUpdatedAt,
    isError,
    error,
    // 실패한 쿼리의 error는 캐시에 남아 컴포넌트를 다시 마운트해도 그대로 읽힌다 —
    // 에러 알림이 "지난번 실패의 잔상"과 "방금 실패"를 구분하려면 확정 시각이 필요하다.
    errorUpdatedAt,
    refetch,
  };
};

export default useGetNaverPartnerStatus;
